import {useEffect, useState} from 'react';
import type {DocumentTypeVisualKind} from './types';

const CATALOG_BASE_URL = (import.meta.env.VITE_ID_SAMPLES_CATALOG as string | undefined)
  ?.trim()
  .replace(/\/+$/, '');

function getDocumentImageUrl(key: string, baseUrl = CATALOG_BASE_URL): string | null {
  if (!baseUrl) return null;
  return `${baseUrl}/${key}.webp`;
}

const PASSPORT_ASPECT_RATIO = 1.42;
const CARD_ASPECT_RATIO = 1.586;

function getFallbackAspectRatio(kind: DocumentTypeVisualKind): number {
  return kind === 'passport' ? PASSPORT_ASPECT_RATIO : CARD_ASPECT_RATIO;
}

type DecodedImage = {
  src: string;
  aspectRatio: number;
};

const decodedImages = new Map<string, DecodedImage>();
const pendingDecodes = new Map<string, Promise<DecodedImage>>();

async function loadImage(url: string): Promise<DecodedImage> {
  const image = new Image();
  image.src = url;
  await image.decode();
  const decoded = {
    src: url,
    aspectRatio: image.naturalHeight ? image.naturalWidth / image.naturalHeight : 0,
  };
  decodedImages.set(url, decoded);
  return decoded;
}

function decodeImage(url: string): Promise<DecodedImage> {
  if (typeof Image === 'undefined') return Promise.resolve({src: url, aspectRatio: 0});
  const decoded = decodedImages.get(url);
  if (decoded) return Promise.resolve(decoded);
  const pending = pendingDecodes.get(url);
  if (pending) return pending;
  const request = loadImage(url).finally(() => pendingDecodes.delete(url));
  pendingDecodes.set(url, request);
  return request;
}

function preloadImage(url: string | null | undefined): Promise<void> {
  if (!url) return Promise.resolve();
  return decodeImage(url).then(
    () => undefined,
    () => undefined,
  );
}

type DocumentVisualImage = {
  src: string | null;
  aspectRatio: number | null;
  hasFailed: boolean;
};

const PENDING: DocumentVisualImage = {src: null, aspectRatio: null, hasFailed: false};
const FAILED: DocumentVisualImage = {src: null, aspectRatio: null, hasFailed: true};

function toVisualImage(decoded: DecodedImage | undefined): DocumentVisualImage {
  if (!decoded) return PENDING;
  return {src: decoded.src, aspectRatio: decoded.aspectRatio || null, hasFailed: false};
}

function useDecodedImage(url: string | null, isRequested = Boolean(url)) {
  const [image, setImage] = useState<DocumentVisualImage>(() =>
    toVisualImage(url ? decodedImages.get(url) : undefined),
  );

  useEffect(() => {
    if (!url) {
      setImage(isRequested ? FAILED : PENDING);
      return;
    }
    setImage(toVisualImage(decodedImages.get(url)));

    let isCancelled = false;
    decodeImage(url)
      .then(decoded => {
        if (!isCancelled) setImage(toVisualImage(decoded));
      })
      .catch(() => {
        if (!isCancelled) setImage(FAILED);
      });

    return () => {
      isCancelled = true;
    };
  }, [url, isRequested]);

  return image;
}

function useDocumentVisualImage(key?: string | null): DocumentVisualImage {
  return useDecodedImage(key ? getDocumentImageUrl(key) : null, Boolean(key));
}

function preloadDocumentVisualImage(key?: string | null) {
  if (key) void preloadImage(getDocumentImageUrl(key));
}

export {
  getDocumentImageUrl,
  getFallbackAspectRatio,
  preloadDocumentVisualImage,
  preloadImage,
  useDecodedImage,
  useDocumentVisualImage,
};
export type {DocumentVisualImage};
